<template>
  <Teleport to="body">
    <Transition name="confirm">
      <div
        v-if="current"
        class="fixed inset-0 bg-black/50 z-[2000] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        @mousedown="onBackdropMousedown"
        @click="onBackdropClick"
        @keydown.esc="cancelConfirm"
        tabindex="-1"
        ref="overlayRef"
      >
        <div class="bg-card rounded-xl shadow-2xl max-w-md w-full">
          <div class="p-6">
            <h3 v-if="current.title" class="text-lg font-semibold text-ink mb-2">{{ current.title }}</h3>
            <p class="text-ink-muted whitespace-pre-line">{{ current.message }}</p>
          </div>
          <div class="bg-surface-muted border-t border-[color:var(--color-border)] p-4 flex justify-end gap-3">
            <button type="button" class="btn-outline" @click="cancelConfirm">
              {{ current.cancelLabel || t('common.cancel') }}
            </button>
            <button type="button" :class="confirmButtonClass" @click="acceptConfirm" ref="confirmButtonRef">
              {{ current.confirmLabel || t('common.confirm') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfirm } from '@/composables/useConfirm';
import { useBackdropDismiss } from '@/composables/useBackdropDismiss';

const { t } = useI18n();
const { current, acceptConfirm, cancelConfirm } = useConfirm();

// Backdrop press+release guard: cancel only when both land on the overlay so a
// selection drag ending on the backdrop doesn't dismiss the dialog (CORA-264 #2).
const { onMousedown: onBackdropMousedown, onClick: onBackdropClick } = useBackdropDismiss(cancelConfirm);

const overlayRef = ref<HTMLElement | null>(null);
const confirmButtonRef = ref<HTMLButtonElement | null>(null);

const confirmButtonClass = computed(() => {
  switch (current.value?.severity) {
    case 'danger':
      return 'btn-danger';
    case 'warning':
      return 'btn-warning';
    default:
      return 'btn-primary';
  }
});

// Focus the confirm button (and overlay for Esc handling) once a request opens.
watch(current, async (next) => {
  if (!next) return;
  await nextTick();
  overlayRef.value?.focus();
  confirmButtonRef.value?.focus();
});
</script>

<style scoped>
.confirm-enter-active,
.confirm-leave-active {
  transition: opacity 0.15s ease;
}
.confirm-enter-from,
.confirm-leave-to {
  opacity: 0;
}
</style>
